<template>
  <div>
    <action-cable
      :params="{ channel: 'StudentStickyNotesChannel' }"
      @sticky_note_requested="studentStickyNotesStore.receiveRequest"
      @sticky_note_request_closed="onRequestClosed"
    />

    <v-dialog :model-value="!!request" max-width="420" @update:model-value="dismiss">
      <div v-if="request" class="sticky-request" data-testid="StickyRequest-dialog">
        <button type="button" class="sticky-request-close" data-testid="StickyRequest-close" @click="dismiss">
          Aizvērt
          <v-icon size="16">fa-light fa-xmark</v-icon>
        </button>

        <p class="sticky-request-intro">Skolotājs Tev uzdevis iesniegt līmlapu!</p>
        <h2 class="sticky-request-question" data-testid="StickyRequest-question">{{ request.question }}</h2>

        <span class="sticky-request-label">Tava līmlapa</span>
        <div class="sticky-request-colors">
          <button
            v-for="option in colors"
            :key="option"
            type="button"
            class="sticky-request-color"
            :class="{ selected: option === color }"
            :style="{ backgroundColor: option }"
            :aria-label="`Krāsa ${option}`"
            data-testid="StickyRequest-color"
            @click="color = option"
          />
        </div>

        <textarea
          ref="text"
          v-model="text"
          class="sticky-request-text"
          :style="{ backgroundColor: color, borderColor: borderColor }"
          placeholder="Ieraksti šeit"
          rows="1"
          data-testid="StickyRequest-text"
          @input="resizeText"
        />

        <button
          type="button"
          class="sticky-request-submit"
          :disabled="!text.trim() || submitting"
          data-testid="StickyRequest-submit"
          @click="submit"
        >
          Iesniegt
        </button>
      </div>
    </v-dialog>

    <button
      v-if="!request && studentStickyNotesStore.requests.length > 0"
      type="button"
      class="sticky-request-reopen"
      data-testid="StickyRequest-reopen"
      @click="studentStickyNotesStore.reopen()"
    >
      <v-icon size="16">fa-light fa-note-sticky</v-icon>
      Iesniegt līmlapu
    </button>
  </div>
</template>

<script lang="ts">
import { useSnackbarStore } from "~/stores/snackBarStore"
import { STICKY_COLORS, mixColor } from "~/stores/stickyBoardStore"
import { useStudentStickyNotesStore } from "~/stores/studentStickyNotesStore"
import type { StickyNoteRequest } from "~/types/stickyBoard"

export default {
  setup() {
    const studentStickyNotesStore = useStudentStickyNotesStore()
    const snackbarStore = useSnackbarStore()
    return { studentStickyNotesStore, snackbarStore, colors: STICKY_COLORS }
  },

  data() {
    return {
      text: "",
      color: STICKY_COLORS[0],
      submitting: false,
    }
  },

  computed: {
    request(): StickyNoteRequest | null {
      return this.studentStickyNotesStore.activeRequest
    },

    borderColor(): string {
      return mixColor(this.color, 0, 0.8)
    },
  },

  watch: {
    "request.id"() {
      this.text = ""
      this.color = STICKY_COLORS[0]
    },
  },

  mounted() {
    this.studentStickyNotesStore.fetchRequests()
  },

  methods: {
    dismiss() {
      if (this.request) {
        this.studentStickyNotesStore.dismiss(this.request.id)
      }
    },

    onRequestClosed({ id }: { id: number }) {
      this.studentStickyNotesStore.removeRequest(id)
    },

    resizeText() {
      const textarea = this.$refs.text as HTMLTextAreaElement
      textarea.style.height = "auto"
      textarea.style.height = `${textarea.scrollHeight}px`
    },

    submit() {
      if (!this.request) { return }

      this.submitting = true
      this.studentStickyNotesStore.submit({
        request: this.request,
        text: this.text.trim(),
        color: this.color,
        successCallback: () => {
          this.submitting = false
          this.snackbarStore.showMessage({ text: "Līmlapa iesniegta!", type: "success" })
        },
        errorCallback: () => {
          this.submitting = false
          this.snackbarStore.showMessage({ text: "Neizdevās iesniegt līmlapu", type: "error" })
        },
      })
    },
  },
}
</script>

<style lang="sass" scoped>
.sticky-request
  position: relative
  display: flex
  flex-direction: column
  gap: 12px
  padding: 28px 24px 24px
  background-color: #FFFFFF
  border-radius: 24px

.sticky-request-close
  display: flex
  align-items: center
  gap: 8px
  align-self: flex-end
  border: none
  background: none
  font-size: 14px
  color: #1F2937
  cursor: pointer

.sticky-request-intro
  margin: 0
  font-size: 14px
  text-align: center
  color: #374151

.sticky-request-question
  margin: 0 0 12px
  min-height: 1.3em
  font-size: 22px
  font-weight: 500
  text-align: center
  color: #111827

.sticky-request-label
  font-size: 14px
  font-weight: 700

.sticky-request-colors
  display: flex
  gap: 8px

.sticky-request-color
  width: 26px
  height: 26px
  padding: 0
  border-radius: 50%
  border: 1px solid rgba(0, 0, 0, 0.2)
  cursor: pointer

  &.selected
    border: 2px solid #374151

.sticky-request-text
  width: 100%
  min-height: 48px
  padding: 12px 16px
  border: 1px solid
  border-radius: 10px
  outline: none
  resize: none
  overflow: hidden
  font-size: 16px
  line-height: 1.4

.sticky-request-submit
  align-self: center
  margin-top: 20px
  padding: 10px 40px
  border: none
  border-radius: 999px
  background-color: #F7D052
  font-size: 16px
  font-weight: 600
  color: #221B00
  cursor: pointer

  &:disabled
    background-color: #E5E5E5
    color: #8A8A8A
    cursor: default

.sticky-request-reopen
  position: fixed
  right: 20px
  bottom: 20px
  z-index: 2000
  display: flex
  align-items: center
  gap: 8px
  padding: 10px 18px
  border: none
  border-radius: 999px
  background-color: #F7D052
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2)
  font-size: 15px
  font-weight: 600
  color: #221B00
  cursor: pointer
</style>
